/**
 * The Jobs tab (Operate › Jobs): every service's scheduled jobs in one list (ScheduledJobs), with Add job (picking the
 * service), edit, pause, Run now and the runs. The same vfs-service API as Configuration had (AppConfigService): the
 * project's overview (`getAppConfig`, each service with its count of jobs), then each service that has jobs
 * (`getServiceConfig`). A service with no job is read once a job is added to it.
 *
 * `focus`: `{ service }` lists that service's jobs first (an older link to Configuration's jobs on a service).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getAppConfig, getServiceConfig } from '../../services/AppConfigService';
import ScheduledJobs from './ScheduledJobs';
import { notifyAppConfigChanged } from '../../hooks/useAppConfigSignal';
import { errorText } from './appConfigModel';
import './AppConfig.css';

export default function JobsPanel({ projectId, focus = null, onAskAI = null }) {
  const [overview, setOverview] = useState(null);
  const [details, setDetails] = useState({}); // service -> its configuration (`jobs`, `canEdit`, `jobsNote`)
  const [error, setError] = useState(null);
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);

  const loadService = useCallback(async(service) => {
    try {
      const detail = await getServiceConfig(projectId, service);
      if (live.current) { setDetails((current) => ({ ...current, [service]: detail })); }
    } catch (e) {
      if (live.current) { setError(e); }
    }
  }, [projectId]);

  const load = useCallback(async() => {
    try {
      const next = await getAppConfig(projectId);
      if (!live.current) { return; }
      setOverview(next);
      setError(null);
      await Promise.all((next.services || []).filter((s) => s.jobs > 0).map((s) => loadService(s.name)));
    } catch (e) {
      if (live.current) { setError(e); }
    }
  }, [projectId, loadService]);

  useEffect(() => { setDetails({}); load(); }, [load]);

  const changed = useCallback((service) => {
    loadService(service);
    notifyAppConfigChanged(projectId);
  }, [loadService, projectId]);

  if (error && !overview) {
    return (
      <div className="cfg-panel" data-testid="jobs-panel">
        <div className="cfg-state is-bad" role="alert"><strong>Jobs are not available</strong><p>{errorText(error)}</p></div>
      </div>
    );
  }
  if (!overview) { return <div className="cfg-panel" data-testid="jobs-panel"><p className="cfg-muted">Loading…</p></div>; }

  const names = (overview.services || []).map((s) => s.name);
  const first = focus?.service && names.includes(focus.service) ? focus.service : null;
  const ordered = first ? [first, ...names.filter((n) => n !== first)] : names;
  const entries = ordered.filter((name) => details[name]).map((name) => ({ service: name, jobs: details[name].jobs || [] }));
  const canEdit = Boolean(overview.canEdit);
  const note = overview.jobsNote || Object.values(details).find((d) => d?.jobsNote)?.jobsNote || null;

  return (
    <div className="cfg-panel" data-testid="jobs-panel">
      {/* The tab's title and what it is for ("Project / Operate / Jobs") are the workspace's (WorkspaceView). */}
      {!canEdit && <p className="cfg-muted">You are a viewer: you see the jobs and their runs, not their logs.</p>}
      {names.length === 0 && <div className="cfg-state"><strong>No services yet</strong><p>A service is a folder under services/. Create one first, then schedule its jobs here.</p></div>}
      {error && <p className="cfg-banner is-fail" role="alert">{errorText(error)}</p>}
      {names.length > 0 && (
        <ScheduledJobs
          canEdit={canEdit}
          entries={entries}
          note={note}
          onAskAI={onAskAI}
          onChanged={changed}
          projectId={projectId}
          services={ordered}
        />
      )}
    </div>
  );
}

JobsPanel.propTypes = {
  focus: PropTypes.shape({ service: PropTypes.string }),
  onAskAI: PropTypes.func,
  projectId: PropTypes.string.isRequired,
};
